import { useMemo } from "react";
import {
  useGetAllConnectedByUserIdQuery,
  useGetBusinessTypeRulesQuery,
} from "./connectivityApi";
import { useCheckMarketerForQuery } from "../../features/user/userApiSlice";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Deduplicate connection items by a composite key.
 * For marketer mode the key only uses user/marketer id; otherwise it uses the full
 * combination of user, shop, marketer, and shop name.
 */
const dedupeConnectionItems = (items, { marketerMode = false } = {}) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const seen = new Map(); // key → index in unique
  const unique = [];

  for (const item of items) {
    const userid =
      item?.userid?._id?.toString?.() ||
      item?.userid?.toString?.() ||
      item?.userid ||
      "";
    const shopidRaw =
      item?.shopid?._id?.toString?.() ||
      item?.shopid?.toString?.() ||
      item?.shopid ||
      "";
    const shopid =
      shopidRaw && shopidRaw !== "null" && shopidRaw !== "undefined"
        ? shopidRaw
        : "";
    const marketerId =
      item?._marketerDocId?.toString?.() || item?._marketerDocId || "";

    if (
      item?.isPlaceholder ||
      (!item?.userid && !item?.shopid && !item?._id && !item?._marketerDocId)
    ) {
      continue;
    }

    if (marketerMode && !userid && !marketerId) continue;

    const key = marketerMode
      ? `marketer|${userid || marketerId}`
      : `${userid}|${shopid || "null"}|${marketerId}`;

    if (seen.has(key)) {
      const idx = seen.get(key);
      const prev = unique[idx];
      const prevShop =
        prev?.shopid?._id?.toString?.() ||
        prev?.shopid?.toString?.() ||
        prev?.shopid ||
        "";
      if ((!prevShop || prevShop === "null") && shopid) {
        unique[idx] = item;
      }
      continue;
    }

    if (!marketerMode && userid && shopid) {
      const personKey = `${userid}|null|${marketerId}`;
      if (seen.has(personKey)) {
        const idx = seen.get(personKey);
        unique[idx] = item;
        seen.delete(personKey);
        seen.set(key, idx);
        continue;
      }
    }

    seen.set(key, unique.length);
    unique.push(item);
  }

  return unique;
};

const createSixDummyItems = (label = "Shop Name") =>
  Array.from({ length: 6 }, () => ({ shopname: label }));

const normalizeItemsToSix = (items) => {
  const unique = dedupeConnectionItems(items);
  if (unique.length === 0) return createSixDummyItems();
  const arr = unique.slice(0, 6);
  while (arr.length < 6)
    arr.push({ shopname: "Shop Name", isPlaceholder: true });
  return arr;
};

const defaultCardTypes = [
  // generalPublic only appears when there are real GP connections (not as a filler)
  { bt: "retailerShop", title: "Retail" },
  { bt: "wholesellerShop", title: "Wholeseller" },
  { bt: "rawmaterialShop", title: "Raw Material" },
  { bt: "manufacturerShop", title: "Manufacturer" },
  { bt: "businessPropertyCompany", title: "Business Property" },
  { bt: "services", title: "Services" },
  { bt: "homemadeShop", title: "Home Made" },
  { bt: "gemShop", title: "Gem" },
  { bt: "foodShop", title: "Food" },
  { bt: "usedShop", title: "Used Items" },
  { bt: "farmingShop", title: "Farming" },
  { bt: "recycledShop", title: "Recycle" },
  { bt: "transportShop", title: "Transport" },
  { bt: "usedVehicleShop", title: "Vehicle" },
  { bt: "jobsCompany", title: "Jobs Company" },
  { bt: "marketerProfile", title: "Marketer Profile" },
];

const makePlaceholderCards = () => {
  return defaultCardTypes.map((item) => ({
    title: "Business Name",
    subtitle: "0 Connections",
    businessType: item.bt,
    count: 0,
    items: createSixDummyItems(),
    isPlaceholder: true,
  }));
};

const resolveIsMarketerProfile = (pathnameOrFlag) => {
  if (typeof pathnameOrFlag === "boolean") return pathnameOrFlag;
  if (pathnameOrFlag && typeof pathnameOrFlag === "object") {
    return (
      pathnameOrFlag.isMarketerProfile === true ||
      pathnameOrFlag.isMarketerProfile === "true"
    );
  }
  const path = String(pathnameOrFlag || "");
  return path === "/marketing-profile" || path.endsWith("/marketing-profile");
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {string} userId
 * @param {string|boolean|{isMarketerProfile?: boolean}} pathnameOrFlag
 *        Pass boolean `true` on /marketing-profile so we never depend on a
 *        stale pathname when resolving personal vs marketer Connectivity.
 */
export default function useConnectivity(userId, pathnameOrFlag) {
  const isMarketerProfile = resolveIsMarketerProfile(pathnameOrFlag);
  const expectedScope = isMarketerProfile ? "marketer" : "personal";

  const {
    isLoading: isConnectedLoading,
    isFetching: isConnectedFetching,
    error: connectedError,
    // NEVER use RTK `data` here — it can hold the previous profile's result
    // while the new scoped request is in flight.
    currentData,
    isUninitialized,
  } = useGetAllConnectedByUserIdQuery(
    { userId, isMarketerProfile },
    {
      skip: !userId,
      refetchOnMountOrArgChange: true,
    },
  );

  // Hard guard: ignore any payload that is not for this profile scope
  const scopeOk =
    currentData &&
    (currentData.profileScope === expectedScope ||
      // Older servers may omit profileScope — only trust when scope matches
      // or when the flag on the request was marketer and response has no
      // personal-only leak markers. Prefer explicit scope.
      (currentData.profileScope == null && !isMarketerProfile));

  const activeConnectedRes =
    currentData &&
    (currentData.profileScope === expectedScope ||
      // Personal page: allow legacy responses without profileScope
      (!isMarketerProfile && currentData.profileScope == null) ||
      // Marketer page: ONLY accept explicit marketer scope (never legacy/personal)
      (isMarketerProfile && currentData.profileScope === "marketer"))
      ? currentData
      : undefined;

  const { data: marketerData, isLoading: isMarketerLoading } =
    useCheckMarketerForQuery(userId, {
      skip: !userId || !isMarketerProfile,
    });

  const marketerFor = marketerData?.data?.MIndustryName?.[0]?.name;

  const { data: rulesRes, isLoading: isRulesLoading } =
    useGetBusinessTypeRulesQuery(marketerFor, { skip: !marketerFor });

  const hasScopedData = Boolean(activeConnectedRes);
  const connectionInitialPending =
    isUninitialized ||
    (!hasScopedData && (isConnectedLoading || isConnectedFetching));
  const marketerExtrasPending =
    isMarketerProfile &&
    !hasScopedData &&
    (isMarketerLoading || (Boolean(marketerFor) && isRulesLoading));

  const isLoading =
    !userId ||
    (!connectedError && connectionInitialPending) ||
    marketerExtrasPending;

  const cards = useMemo(() => {
    if (!activeConnectedRes) return makePlaceholderCards();

    const payload =
      activeConnectedRes?.connectedBT != null
        ? activeConnectedRes
        : activeConnectedRes?.data?.connectedBT != null
          ? activeConnectedRes.data
          : (activeConnectedRes?.data ?? activeConnectedRes ?? {});

    let connectedBT = payload?.connectedBT ?? [];
    if (!Array.isArray(connectedBT)) connectedBT = [];

    if (connectedBT.length === 0) return makePlaceholderCards();

    // Marketing: only groups that contain real connection tiles
    if (isMarketerProfile) {
      connectedBT = connectedBT.filter(
        (g) =>
          Array.isArray(g?.items) &&
          g.items.some(
            (it) =>
              it &&
              !it.isPlaceholder &&
              (it.userid || it.shopid || it._id || it._marketerDocId),
          ),
      );
      if (connectedBT.length === 0) return makePlaceholderCards();
    }

    const isMarketerBT = (bt) => {
      const n = String(bt || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
      return (
        n === "marketerprofile" ||
        n === "marketer" ||
        n === "marketershop" ||
        n === "approvedmarketers"
      );
    };

    const formatConnectivityTitle = (bt) => {
      if (!bt) return "Business Name";
      if (bt === "generalPublic") return "generalPublic";
      if (bt === "marketerProfile") return "Marketer Profile";
      if (bt === "retailerShop") return "Retail";
      if (bt === "wholesellerShop") return "Wholeseller";
      if (bt === "rawmaterialShop") return "Raw Material";
      if (bt === "manufacturerShop") return "Manufacturer";
      if (bt === "businessPropertyCompany") return "Business Property";
      if (bt === "services") return "Services";
      if (bt === "homemadeShop") return "Home Made";
      if (bt === "gemShop") return "Gem";
      if (bt === "foodShop") return "Food";
      if (bt === "usedShop") return "Used Items";
      if (bt === "recycledShop") return "Recycle";
      if (bt === "farmingShop") return "Farming";
      if (bt === "usedVehicleShop") return "Vehicle";
      if (bt === "transportShop") return "Transport";
      if (bt === "jobsCompany") return "Jobs Company";
      return (
        bt.charAt(0).toUpperCase() + bt.slice(1).replace(/([A-Z])/g, " $1")
      );
    };

    const canonicalFrontendBT = (btVal) => {
      const n = String(btVal || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
      if (!n) return "generalPublic";
      if (n === "retail" || n === "retailer" || n === "retailershop")
        return "retailerShop";
      if (n === "service" || n === "services") return "services";
      if (n === "wholeseller" || n === "wholesellershop")
        return "wholesellerShop";
      if (n === "rawmaterial" || n === "rawmaterialshop")
        return "rawmaterialShop";
      if (n === "manufacturer" || n === "manufacturershop")
        return "manufacturerShop";
      if (
        n === "businesspropertycompany" ||
        n === "businessproperty" ||
        n === "property"
      )
        return "businessPropertyCompany";
      if (
        n === "homemade" ||
        n === "homemadeshop" ||
        n === "homemadeitem" ||
        n === "homemadeshopitem"
      )
        return "homemadeShop";
      if (n === "gem" || n === "gemshop") return "gemShop";
      if (n === "food" || n === "foodshop") return "foodShop";
      if (n === "used" || n === "usedshop" || n === "useditems") return "usedShop";
      if (n === "recycled" || n === "recycledshop" || n === "recycle")
        return "recycledShop";
      if (n === "farming" || n === "farmingshop") return "farmingShop";
      if (n === "usedvehicle" || n === "usedvehicleshop" || n === "vehicle")
        return "usedVehicleShop";
      if (n === "transport" || n === "transportshop") return "transportShop";
      if (n === "jobscompany" || n === "company" || n === "jobs")
        return "jobsCompany";
      if (isMarketerBT(btVal)) return "marketerProfile";
      if (n === "generalpublic") return "generalPublic";
      return btVal;
    };

    const mergedByBT = new Map();
    for (const apiData of connectedBT) {
      const rawBt = apiData.businessType || "generalPublic";
      const bt = canonicalFrontendBT(rawBt);
      const existing = mergedByBT.get(bt);
      const items = [
        ...(existing?.items || []),
        ...(Array.isArray(apiData.items) ? apiData.items : []),
      ];
      mergedByBT.set(bt, { businessType: bt, items });
    }

    const realCards = [];
    for (const [, apiData] of mergedByBT) {
      const bt = apiData.businessType || "generalPublic";
      const uniqueItems = dedupeConnectionItems(apiData.items, {
        marketerMode: bt === "marketerProfile",
      });

      // Skip empty groups — including empty generalPublic shells.
      // (Old exception kept empty GP on /user-profile even with 0 connections.)
      if (uniqueItems.length === 0) continue;

      realCards.push({
        title: formatConnectivityTitle(bt),
        subtitle: `${uniqueItems.length} Connections`,
        businessType: bt,
        count: uniqueItems.length,
        items: normalizeItemsToSix(uniqueItems),
        isPlaceholder: false,
      });
    }

    if (realCards.length === 0) return makePlaceholderCards();

    if (isMarketerProfile) {
      const priority = [
        "generalPublic",
        "retailerShop",
        "wholesellerShop",
        "rawmaterialShop",
        "manufacturerShop",
        "businessPropertyCompany",
        "services",
        "homemadeShop",
        "gemShop",
        "foodShop",
        "usedShop",
        "farmingShop",
        "marketerProfile",
      ];
      realCards.sort((a, b) => {
        const aReal = a.count > 0 ? 1 : 0;
        const bReal = b.count > 0 ? 1 : 0;
        if (aReal !== bReal) return bReal - aReal;
        const ai = priority.indexOf(a.businessType);
        const bi = priority.indexOf(b.businessType);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
    } else {
      const gpIdx = realCards.findIndex(
        (c) => c.businessType === "generalPublic",
      );
      if (gpIdx > 0) {
        const [gpCard] = realCards.splice(gpIdx, 1);
        realCards.unshift(gpCard);
      }
    }

    const totalCards = [...realCards];
    const existingNormBts = new Set(
      totalCards.map((c) =>
        String(c?.businessType || "")
          .toLowerCase()
          .replace(/[\s_-]+/g, ""),
      ),
    );

    for (const def of defaultCardTypes) {
      if (totalCards.length >= 12) break;
      const nDef = String(def.bt).toLowerCase().replace(/[\s_-]+/g, "");
      if (!existingNormBts.has(nDef)) {
        totalCards.push({
          title: "Business Name",
          subtitle: "0 Connections",
          businessType: def.bt,
          count: 0,
          items: createSixDummyItems(),
          isPlaceholder: true,
        });
        existingNormBts.add(nDef);
      }
    }

    while (totalCards.length < 12) {
      totalCards.push({
        title: "Business Name",
        subtitle: "0 Connections",
        // Neutral placeholder BT — do not stamp generalPublic on empty fillers
        // or Profile Connectivity shows a default GP box with 0 connections.
        businessType: "Business Name",
        count: 0,
        items: createSixDummyItems(),
        isPlaceholder: true,
      });
    }

    return totalCards;
  }, [activeConnectedRes, rulesRes, isMarketerProfile, marketerFor]);

  return {
    cards,
    isLoading,
    // True during background refetch while cards stay visible
    isFetching: Boolean(isConnectedFetching && hasScopedData),
    error: connectedError,
    message: activeConnectedRes?.message || activeConnectedRes?.data?.message,
    profileScope: activeConnectedRes?.profileScope || null,
    scopeOk,
  };
}
